import React, { FC, useCallback, useMemo, useState } from 'react';

import * as RE from '~/reducers/experiences';

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';

import { FILTER_KEYS, FILTER_RANGE_KEYS, FilterKeys, themes } from '~/consts/experiences';

import * as ES from '~/services/ExperiencesService';

import * as LE from '../components';

import { ExperienceFilterContent } from './Content/ExperienceFilterContent';
import { FieldValueParams } from './Experiences';

type FiltersProps = {
  ready?: boolean;
  filters: ES.Filters;
  searchOptions?: Array<string>;
  toggleOptions?: Array<string>;
  fieldSearchValue: string;
  resetFilters: () => void;
  categories: RE.CategoriesState;
  onSelectToggle?: (opt: string) => void;
  fetchExperiencesWithFilters: () => void;
  toggleSelected?: { [key: string]: boolean };
  handleSearchFieldValue: (value: string) => void;
  onSelectLocation?: (address: string) => Promise<void>;
  handleFieldValue: (params: FieldValueParams) => void;
  setFiltersByKey: (key: 'categories' | 'status', value: string) => void;
  searchModeSelected: SearchModeOptions;
  setSearchModeSelected: (option: SelectChangeEvent<SearchModeOptions>) => void;
  actions?: Array<Action>;
  searchModeOptions?: Array<SearchModeOptions>;
};

type Action = {
  label: string;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
};

export enum SearchModeOptions {
  ID = 'idIn',
  TITLE = 'title',
  VENDOR = 'vendor',
  LOCATION = 'location',
}

const searchModeOptionsLabels = {
  [SearchModeOptions.ID]: 'ID',
  [SearchModeOptions.TITLE]: 'Title',
  [SearchModeOptions.VENDOR]: 'Vendor',
  [SearchModeOptions.LOCATION]: 'Location',
};

export const ExperiencesFormFilters: FC<FiltersProps> = ({
  ready,
  filters,
  categories,
  resetFilters,
  searchOptions,
  toggleOptions,
  toggleSelected = { table: true },
  onSelectToggle,
  setFiltersByKey,
  fieldSearchValue,
  onSelectLocation,
  handleFieldValue,
  handleSearchFieldValue,
  fetchExperiencesWithFilters,
  searchModeSelected,
  setSearchModeSelected,
  actions,
  searchModeOptions: searchModeOptionsProp,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const countFiltersSelected = useCallback(() => {
    const arr = Object.entries(filters).filter((item) => {
      if (FILTER_RANGE_KEYS.includes(item[0])) {
        return item[1]?.min || item[1]?.max;
      }
      if (FILTER_KEYS.includes(item[0] as FilterKeys) && item[1]?.length > 0) {
        return item[1];
      }
    });

    return arr.length;
  }, [filters.categories, filters.status, filters.price, filters.rating, filters.providerIn]);

  const applyFilters = useCallback(() => {
    fetchExperiencesWithFilters();
    handleClose();
  }, [filters]);

  const handleClose = useCallback(() => setIsOpen(false), []);

  const searchModeOptions = useMemo(() => {
    if (!searchModeOptionsProp) {
      return searchModeOptionsLabels;
    }
    return searchModeOptionsProp?.reduce(
      (acc, option) => ({
        ...acc,
        [option]: searchModeOptionsLabels[option],
      }),
      {},
    );
  }, [searchModeOptionsProp]);

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box>
          <Select onChange={setSearchModeSelected} value={searchModeSelected} fullWidth>
            {Object.entries(searchModeOptions).map(([key, label]: [string, string]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box flexGrow={1}>
          {searchModeSelected == SearchModeOptions.LOCATION && (
            <LE.FieldSearch
              autoComplete="off"
              name="experiences-location"
              value={fieldSearchValue || ''}
              onClickOption={onSelectLocation}
              options={ready && searchOptions}
              onChange={handleSearchFieldValue}
              placeholder="Search experiences by location"
              fullWidth
            />
          )}
          {searchModeSelected == SearchModeOptions.ID && (
            <LE.Field
              autoComplete="off"
              name="experiences-id"
              value={fieldSearchValue || ''}
              onChange={(params) => handleSearchFieldValue(params.value)}
              placeholder="Search experiences by ID separateds by comma"
              fullWidth
            />
          )}

          {searchModeSelected == SearchModeOptions.TITLE && (
            <LE.Field
              autoComplete="off"
              name="experiences-title"
              value={fieldSearchValue || ''}
              onChange={(params) => handleSearchFieldValue(params.value)}
              placeholder="Search experiences by title"
              fullWidth
            />
          )}

          {searchModeSelected == SearchModeOptions.VENDOR && (
            <LE.Field
              autoComplete="off"
              name="experiences-vendor"
              value={fieldSearchValue || ''}
              onChange={(params) => handleSearchFieldValue(params.value)}
              placeholder="Search experiences by vendor"
              fullWidth
            />
          )}
        </Box>

        <Box>
          <LE.FlexWrapper justify="flex-end">
            <Stack direction="row" spacing={2} alignItems="center" divider={<LE.LineDivisor mx="15px" />}>
              <Badge badgeContent={countFiltersSelected().toString()} color="secondary">
                <Button variant="contained" onClick={() => setIsOpen(true)}>
                  Filters
                </Button>
              </Badge>
              {toggleOptions?.length > 1 && (
                <LE.Toggle options={toggleOptions} onClick={onSelectToggle} selected={toggleSelected} />
              )}

              {!!actions?.length && (
                <ButtonGroup variant="contained">
                  {actions.map((action) => (
                    <Button
                      onClick={action.onClick}
                      disabled={action.loading || action.disabled}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {action.loading ? 'Loading...' : action.label}
                    </Button>
                  ))}
                </ButtonGroup>
              )}
            </Stack>
          </LE.FlexWrapper>
        </Box>
      </Stack>

      <Drawer open={isOpen} anchor="left" onClose={handleClose} title="Experience Filters">
        <Box display="flex" justifyContent="space-between" alignItems="center" px={2}>
          <Typography variant="h4" color={themes?.primaryBlue}>
            Experience Filters
          </Typography>
          <IconButton onClick={handleClose}>
            <CancelOutlinedIcon fontSize="large" />
          </IconButton>
        </Box>
        <ExperienceFilterContent
          filters={filters}
          categories={categories}
          setFiltersByKey={setFiltersByKey}
          handleFieldValue={handleFieldValue}
        />
        <LE.FlexWrapper direction="column">
          <LE.FlexWrapper mt="15px">
            <LE.Button title="Apply Filters" onClick={applyFilters} />
          </LE.FlexWrapper>

          <LE.FlexWrapper mt="15px">
            <LE.OutlineButton title="Reset Filters" onClick={resetFilters} borderColor={themes.fourthBlue} />
          </LE.FlexWrapper>

          <LE.FlexWrapper my="15px">
            <LE.OutlineButton title="Cancel" onClick={handleClose} borderColor={themes?.primaryGrey} />
          </LE.FlexWrapper>
        </LE.FlexWrapper>
      </Drawer>
    </>
  );
};
